import { memo, useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { InputField, NewTabLink, Tooltip, TurnstileWithSkeleton } from '@xver/components/Atoms';
import Button from '@xver/components/Atoms/Button/Button';

import { Checkbox } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { PAGE_TYPE, type PageType } from '../../constants';
import { useSignUp } from './hooks/useSignUp';

const formatPhoneNumber = (phoneNumber: string) => {
    const digits = phoneNumber.replace(/\D/g, '').slice(0, 10);

    if (digits.length <= 3) {
        return digits;
    }

    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

interface PolicyItem {
    key: keyof typeof checkboxesInitial;
    labelKey: 'refundPolicy' | 'termsOfService' | 'privacyPolicy';
    url: string;
}

const checkboxesInitial = {
    refundPolicy: false,
    termsOfService: false,
    privacyPolicy: false,
};

const POLICIES: PolicyItem[] = [
    {
        key: 'refundPolicy',
        labelKey: 'refundPolicy',
        url: 'https://xver.cloud/th/legal/refund-policy/',
    },
    {
        key: 'termsOfService',
        labelKey: 'termsOfService',
        url: 'https://xver.cloud/th/legal/terms-of-service/',
    },
    {
        key: 'privacyPolicy',
        labelKey: 'privacyPolicy',
        url: 'https://xver.cloud/th/legal/privacy-policy/',
    },
];

interface PolicyCheckboxProps {
    checked: boolean;
    policyLabel: string;
    policyUrl: string;
    onChange: (checked: boolean) => void;
}

const PolicyCheckbox = memo<PolicyCheckboxProps>(
    ({ checked, policyLabel, policyUrl, onChange }) => (
        <Checkbox
            size="xs"
            checked={checked}
            label={
                <span className="text-[13px] text-fg">
                    <Trans
                        i18nKey="signUp.policy.agreeToPolicy"
                        ns="auth"
                        values={{ policy: policyLabel }}
                        components={{
                            1: (
                                <NewTabLink
                                    href={policyUrl}
                                    className="text-blue-400 light:text-blue-700 hover:underline"
                                />
                            ),
                        }}
                    />
                </span>
            }
            color="blue"
            variant="filled"
            onChange={(event) => onChange(event.currentTarget.checked)}
        />
    )
);

interface SignUpFormProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

function SignUpForm({ setPageType }: SignUpFormProps) {
    const { t } = useTranslation(['auth', 'validation']);
    const [checkboxes, setCheckboxes] = useState(checkboxesInitial);

    const allChecked =
        checkboxes.refundPolicy && checkboxes.termsOfService && checkboxes.privacyPolicy;

    const {
        isSubmitting,
        errors,
        register,
        setValue,
        watch,
        onSubmit: handleSignUp,
    } = useSignUp({ setPageType });

    const handlePhoneChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const formatted = formatPhoneNumber(e.target.value);
            setValue('phoneNumber', formatted, {
                shouldValidate: true,
                shouldDirty: true,
            });
        },
        [setValue]
    );

    const handlePolicyChange = useCallback(
        (key: keyof typeof checkboxesInitial, checked: boolean) => {
            setCheckboxes((prev) => ({ ...prev, [key]: checked }));
        },
        []
    );

    const handleSignIn = useCallback(() => {
        setPageType(PAGE_TYPE.SIGN_IN);
    }, [setPageType]);

    const policyItems = useMemo(
        () =>
            POLICIES.map((p) => ({
                ...p,
                label: t(`signUp.policy.${p.labelKey}`),
            })),
        [t]
    );

    return (
        <form
            autoComplete="off"
            onSubmit={handleSignUp}
            className="w-full flex flex-col justify-start items-center"
        >
            <div
                className="w-full flex flex-col justify-center items-center gap-4"
                style={{ maxWidth: '400px' }}
            >
                <div className="w-full flex flex-col justify-center items-center gap-3">
                    <InputField
                        label={t('signUp.emailLabel')}
                        size="sm"
                        type="email"
                        {...register('email', {
                            required: t('validation:email.required'),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('validation:email.invalid'),
                            },
                        })}
                        placeholder={t('signUp.emailLabel')}
                        error={errors.email?.message}
                        required
                    />
                    <InputField
                        label={t('signUp.passwordLabel')}
                        size="sm"
                        type="password"
                        {...register('password', {
                            required: t('validation:password.required'),
                            minLength: {
                                value: 8,
                                message: t('validation:password.tooShort'),
                            },
                        })}
                        placeholder={t('signUp.passwordLabel')}
                        error={errors.password?.message}
                        passwordReveal
                        required
                    />
                    <InputField
                        label={t('signUp.confirmPasswordLabel')}
                        size="sm"
                        type="password"
                        {...register('confirmPassword', {
                            required: t('validation:confirmPassword.required'),
                            validate: (value) =>
                                value === watch('password') ||
                                (t('validation:password.mismatch') as string),
                        })}
                        placeholder={t('signUp.confirmPasswordLabel')}
                        error={errors.confirmPassword?.message}
                        passwordReveal
                        required
                    />
                    <InputField
                        label={t('signUp.phoneLabel')}
                        size="sm"
                        type="tel"
                        {...register('phoneNumber', {
                            required: t('validation:phone.required'),
                            pattern: {
                                value: /^\d{3}-\d{3}-\d{4}$/,
                                message: t('validation:phone.invalid'),
                            },
                        })}
                        onChange={handlePhoneChange}
                        value={watch('phoneNumber') ?? ''}
                        placeholder={t('signUp.phonePlaceholderExample')}
                        error={errors.phoneNumber?.message}
                        required
                    />
                </div>

                <div className="w-full flex flex-col justify-start items-start gap-1">
                    <span className="text-xs text-fg/65">{t('signUp.letUsKnowYouAreHuman')}</span>
                    <TurnstileWithSkeleton
                        theme="dark"
                        size="flexible"
                        className="captcha"
                        onVerify={(token) => setValue('turnstileToken', token)}
                        onExpire={() => setValue('turnstileToken', '')}
                        onError={() => setValue('turnstileToken', '')}
                    />
                </div>

                <div className="w-full flex flex-col justify-start items-start gap-2">
                    {policyItems.map((policy) => (
                        <PolicyCheckbox
                            key={policy.key}
                            checked={checkboxes[policy.key]}
                            policyLabel={policy.label}
                            policyUrl={policy.url}
                            onChange={(checked) => handlePolicyChange(policy.key, checked)}
                        />
                    ))}
                </div>

                {allChecked ? (
                    <Button
                        width="100%"
                        type="submit"
                        isLoading={isSubmitting}
                        rightIcon={<IconArrowRight size={16} stroke={1.6} />}
                    >
                        {t('signUp.submit')}
                    </Button>
                ) : (
                    <Tooltip
                        className="w-full"
                        content={t('signUp.policy.tooltipMustAgree')}
                        position="bottom"
                    >
                        <Button
                            width="100%"
                            type="submit"
                            isLoading={isSubmitting}
                            rightIcon={<IconArrowRight size={16} stroke={1.6} />}
                            disabled
                        >
                            {t('signUp.submit')}
                        </Button>
                    </Tooltip>
                )}

                <div className="flex items-center gap-1">
                    <span className="text-sm text-fg/78">{t('signUp.haveAccount')}</span>
                    <Link
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSignIn();
                        }}
                        className="ml-1 text-sm text-blue-400 light:text-blue-700 hover:underline transition-allease-in-out duration-150"
                    >
                        {t('signUp.signInLink')}
                    </Link>
                </div>
            </div>
        </form>
    );
}

export default memo(SignUpForm);
